/**
 * CallRail Wire-Format Transformers (Story 22.1, AC #4, AC #12)
 *
 * CallRail's API returns snake_case field names (`total_calls`,
 * `customer_phone_number`, `start_time`, etc.). ProvenLeap's record schema
 * uses lowerCamelCase consistently across all adapters. The transformers
 * here rewrite the wire-format records to the ProvenLeap shape per
 * endpoint type, applying TWO defensive layers along the way:
 *
 *  1. **`stripCsvInjectionPrefix` on every string-valued field** that
 *     could end up in a CSV export (Round 3 Red Team Attack #4). Leading
 *     `=`, `+`, `@`, `-`, tab, CR characters are LOOP-stripped to defend
 *     against pathological customer-supplied data like
 *     `customer_name = "=cmd|/c calc!A1"` triggering Excel formula
 *     execution on export.
 *  2. **Recording URL stripping** on every call record (Round 3 Red Team
 *     Attack #2). CallRail's `recording` field is a signed URL pointing
 *     at `/v3/a/{account_id}/calls/{call_id}/recording` — if a buggy
 *     widget echoed this cross-tenant, it would be a data leak. Story
 *     22.1 deliberately does NOT persist recording URLs; widgets that
 *     need them request via a dedicated tenant-scoped route (deferred to
 *     Story 22.5). The transformer strips `recording` AND `recording_player`
 *     from every record before return.
 *
 * **`_reportType` discriminator (per AC #4):** every record carries a
 * `_reportType` literal identifying which endpoint produced it. Mirrors
 * the Ahrefs 20.1 / Semrush 20.4 / Moz 20.7 pattern — downstream consumers
 * use this to switch on record shape.
 */

import { stripCsvInjectionPrefix } from "./pii";

/**
 * The five `_reportType` literals Story 22.1 emits. Frozen union — adding
 * a new endpoint requires both adding a new transformer here AND extending
 * this type.
 *
 * Pass 1 review fix: `"text-messages"` was missing from this union even
 * though `fetchCallRailTextMessages` emits it on every SMS record.
 * Downstream code switching on the union would silently miss SMS rows.
 */
export type CallRailReportType =
  | "calls-summary"
  | "calls-timeseries"
  | "calls-list"
  | "form-submissions"
  | "text-messages";

// ============================================================================
// snake_case → lowerCamelCase keys
// ============================================================================

/**
 * Convert a snake_case string to lowerCamelCase. Pure function.
 * `total_calls` → `totalCalls`, `customer_phone_number` → `customerPhoneNumber`.
 *
 * Leaves already-camelCase strings unchanged. Handles mixed-shape inputs
 * (e.g. `formattedAverageDuration` stays as-is).
 */
function snakeToCamel(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

/**
 * Apply CSV-injection sanitization to every string-valued field in a
 * record. Recurses into arrays of strings (e.g. `tags`,
 * `keywords_spotted`) and one level deep into nested-object fields
 * (e.g. `milestones`). Other types pass through unchanged. Pure
 * function — returns a new shallow copy.
 *
 * Pass 1 review fix: previously `tags: ["=cmd|/c calc!A1"]` flowed
 * through unchanged because the helper only checked top-level strings.
 * Now arrays-of-strings AND nested objects are sanitized too — both
 * are real shapes returned by CallRail (`tags`, `keywords_spotted`,
 * `milestones`).
 */
/**
 * Reserved JS-runtime keys — same defense as `pii.ts FORBIDDEN_KEYS`.
 * Pass 2 review fix.
 */
const FORBIDDEN_SANITIZE_KEYS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

function sanitizeStringsImpl(value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    return stripCsvInjectionPrefix(value);
  }
  // Pass 2 review fix: depth cap raised to 6 — `milestones[].steps[].metadata`
  // shapes from CallRail's multi-touch attribution payloads naturally hit
  // depth 4-5, so a depth-3 cap was silently dropping sanitization on
  // legitimate nested fields.
  if (depth >= 6 || value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeStringsImpl(item, depth + 1));
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    // Pass 3 review fix: use `{}` not `Object.create(null)` — same
    // reasoning as pii.ts. FORBIDDEN_SANITIZE_KEYS skip is the actual
    // prototype-pollution defense.
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (FORBIDDEN_SANITIZE_KEYS.has(k)) continue;
      out[k] = sanitizeStringsImpl(v, depth + 1);
    }
    return out;
  }
  return value;
}

function sanitizeStrings<T extends Record<string, unknown>>(
  record: T,
): Record<string, unknown> {
  return sanitizeStringsImpl(record, 0) as Record<string, unknown>;
}

/**
 * Strip ANY field whose key contains `recording` (case-insensitive) from a
 * record. Story 22.1 does NOT persist recording URLs; Story 22.5 widens this
 * to a regex-based whitelist filter so future endpoint families OR schema
 * drift cannot re-introduce upstream URLs.
 *
 * Round 3 Red Team Attack #2 defense (Story 22.1) + Story 22.5 AC #8/#21
 * cradle-to-grave invariant.
 */
function stripRecordingUrls(
  record: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    // Case-insensitive match against any key containing `recording`.
    // Matches: recording, recording_player, recordingUrl, recording-url,
    //          recordingPlayer, recording_link, etc.
    if (/recording/i.test(key)) continue;
    out[key] = value;
  }
  return out;
}

// ============================================================================
// Per-endpoint transformers
// ============================================================================

/**
 * Wire-format shape for the `/calls/summary.json` `total_results` object
 * (per `apidocs.callrail.com/` verified 2026-05-14). Loose `Record` typing
 * because CallRail's field set is provisional and the docs don't expose a
 * formal schema; the transformer doesn't need every field, just the ones
 * it remaps.
 */
export type CallRailSummaryApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/calls/summary.json`.
 *
 * The endpoint returns either a single `total_results` object (when
 * `group_by` is omitted) OR an array of `grouped_results` objects (when
 * grouped). This transformer handles ONE row; the caller iterates over the
 * grouped array.
 *
 * @param row - Raw `total_results` or `grouped_results[i]` row.
 * @param context.accountId - The CallRail account this row belongs to.
 * @param context.companyId - Optional company filter that produced this row.
 * @param context.groupByValue - The `group_by` bucket value (only set when
 *   grouped).
 */
export function transformCallRailSummaryRow(
  row: CallRailSummaryApiRow,
  context: {
    readonly accountId: string;
    readonly companyId?: string | null;
    readonly groupByValue?: string | null;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);
  return {
    _reportType: "calls-summary",
    accountId: context.accountId,
    ...(context.companyId !== undefined && context.companyId !== null
      ? { companyId: context.companyId }
      : {}),
    ...(context.groupByValue !== undefined && context.groupByValue !== null
      ? { groupByValue: context.groupByValue }
      : {}),
    ...sanitized,
  };
}

/**
 * Wire-format shape for `/calls/timeseries.json` `data[i]` rows.
 */
export type CallRailTimeseriesApiRow = Record<string, unknown> & {
  readonly date?: string;
};

/**
 * Transform a single row from `/calls/timeseries.json`. Each row
 * represents one interval (day/week/month per the `granularity` param).
 */
export function transformCallRailTimeseriesRow(
  row: CallRailTimeseriesApiRow,
  context: {
    readonly accountId: string;
    readonly companyId?: string | null;
    readonly granularity: string;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);
  return {
    _reportType: "calls-timeseries",
    accountId: context.accountId,
    ...(context.companyId !== undefined && context.companyId !== null
      ? { companyId: context.companyId }
      : {}),
    granularity: context.granularity,
    ...sanitized,
  };
}

/**
 * Wire-format shape for `/calls.json` `calls[i]` rows. Defaults to a
 * loose `Record` because CallRail's full default-field list (16 fields)
 * plus optional opt-in fields (38+) is broad and the docs don't expose a
 * formal schema.
 */
export type CallRailCallApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/calls.json`. STRIPS `recording` /
 * `recording_player` URLs per Round 3 Red Team Attack #2 — recording URLs
 * never enter the persisted record shape in Story 22.1.
 *
 * **PII is NOT redacted here** — the transformer is on the
 * `fetchMetrics` happy-path BEFORE records flow into the tenant-scoped
 * KV cache + D1 persistence. PII redaction happens at the LOG boundary
 * via `sanitizeCallRailPii`. This separation is deliberate: tenant-scoped
 * persistence keeps PII secure, but log-output (which spans tenants
 * forensically) must redact.
 */
export function transformCallRailCallRow(
  row: CallRailCallApiRow,
  context: {
    readonly accountId: string;
    readonly companyId?: string | null;
  },
): Record<string, unknown> {
  const stripped = stripRecordingUrls(row);
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stripped)) {
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);
  return {
    _reportType: "calls-list",
    accountId: context.accountId,
    ...(context.companyId !== undefined && context.companyId !== null
      ? { companyId: context.companyId }
      : {}),
    ...sanitized,
  };
}

/**
 * Wire-format shape for `/form_submissions.json` `form_submissions[i]` rows.
 */
export type CallRailFormSubmissionApiRow = Record<string, unknown>;

/**
 * Transform a single row from `/form_submissions.json`. Same PII /
 * recording-URL contract as the calls transformer (PII flows through
 * for persistence; logging gate redacts).
 */
export function transformCallRailFormSubmissionRow(
  row: CallRailFormSubmissionApiRow,
  context: {
    readonly accountId: string;
    readonly companyId?: string | null;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);
  return {
    _reportType: "form-submissions",
    accountId: context.accountId,
    ...(context.companyId !== undefined && context.companyId !== null
      ? { companyId: context.companyId }
      : {}),
    ...sanitized,
  };
}

/**
 * Wire-format shape for `/text-messages.json` `text_messages[i]` rows.
 *
 * Pass 1 review fix: this transformer was previously inlined inside
 * `fetchCallRailTextMessages` and did NOT pass records through
 * `sanitizeStrings`. SMS bodies are user-controlled content (the
 * caller types the message) — a tag-like field containing
 * `=cmd|/c calc!A1` would have flowed straight to CSV export without
 * sanitization. Now SMS rows go through the same shape as call /
 * form-submission rows.
 */
export type CallRailTextMessageApiRow = Record<string, unknown>;

export function transformCallRailTextMessageRow(
  row: CallRailTextMessageApiRow,
  context: {
    readonly accountId: string;
    readonly companyId?: string | null;
  },
): Record<string, unknown> {
  const camelKeyed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    camelKeyed[snakeToCamel(key)] = value;
  }
  const sanitized = sanitizeStrings(camelKeyed);
  return {
    _reportType: "text-messages",
    accountId: context.accountId,
    ...(context.companyId !== undefined && context.companyId !== null
      ? { companyId: context.companyId }
      : {}),
    ...sanitized,
  };
}
